import PropTypes from "prop-types";
import React from "react";

import { Icon, iconPropTypes, Text, textPropTypes } from "@swa-ui/core";
import { classNames } from "@swa-ui/string";

import { ItineraryStation } from "../ItineraryStation/ItineraryStation";
import styles from "./ItineraryStations.module.scss";

/**
 * Displays the departure and arrival station codes for a trip.  For example:  "DAL - ATL".
 */
export const ItineraryStations = (props) => {
  const {
    arrivalStationCode,
    className,
    departureStationCode,
    returnStationCode,
    stationsSeparator = "-",
    styleType = "primary",
    textProps,
  } = props;

  return (
    <span className={classNames(className, styles.layout)}>
      <ItineraryStation textProps={getStylingProps()} stationCode={departureStationCode} />
      {renderSeparator()}
      <ItineraryStation textProps={getStylingProps()} stationCode={arrivalStationCode} />
      {!!returnStationCode && (
        <>
          {renderSeparator()}
          <ItineraryStation textProps={getStylingProps()} stationCode={returnStationCode} />
        </>
      )}
    </span>
  );

  function renderSeparator() {
    return typeof stationsSeparator === "string" ? (
      <Text {...getStylingProps()}>{stationsSeparator}</Text>
    ) : (
      <div className={styles.iconContainer}>
        <Icon {...stationsSeparator} />
      </div>
    );
  }

  function getStylingProps() {
    const styleTypeTextProps = {
      primary: {
        color: "primary",
        size: "xlarge",
      },
      secondary: {
        size: "medium",
      },
    };

    return textProps ?? styleTypeTextProps[styleType];
  }
};

ItineraryStations.propTypes = {
  /** The arrival station code for the trip. */
  arrivalStationCode: PropTypes.string.isRequired,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** The departure station code for the trip. */
  departureStationCode: PropTypes.string.isRequired,

  /** Indicates if the stations data show disabled color. */
  disabled: PropTypes.bool,

  /** The return airport station code for multi-city */
  returnStationCode: PropTypes.string,

  /** Indicates what should be displayed to separate the station codes.
   * If a string is provided, it will be used as the separator.
   * If an object is provided, it will be used as the icon properties to be displayed.
   */
  stationsSeparator: PropTypes.oneOfType([PropTypes.shape(iconPropTypes), PropTypes.string]),

  /** Option to quickly theme the component text size and color. Will be ignored if textProps is defined. */
  styleType: PropTypes.oneOf(["primary", "secondary"]),

  /** Props passed to the underlying Text component. See Text for more details. */
  textProps: PropTypes.shape(textPropTypes),
};
